import DateRangeSelectors from "components/shared/DateRangeSelectors";
import { useDateSelectorShortcuts } from "components/shared/DateRangeSelectors/utils";
import type { FilterOption } from "components/shared/FiltersAutocomplete";
import FiltersAutocomplete from "components/shared/FiltersAutocomplete";
import { isObjectEmpty } from "helpers/utils";
import type { ChangeEvent, SetStateAction } from "react";
import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { putCompanySettings } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";
import type { AnalyticsFilters, IDatesRange } from "types";

import type { StoredPreferences } from "../Dashboard/utils";
import { ANALYTICS_DATE_PICKER_MAX_END, settingsTransform } from "../Dashboard/utils";

export type TitlesPaginationData = {
    handleQueryTitle: (e: React.ChangeEvent<HTMLInputElement>) => void;
    setOffsetPaginationTitles: React.Dispatch<SetStateAction<number>>;
    requestStatusTitle: TFetchStatus;
};

interface IFilters {
    isLoading: boolean;
    periodsDateRange: IDatesRange;
    titlesPaginationData: TitlesPaginationData;
    filters?: AnalyticsFilters;
    onOptionSelect: (optionKey: string, value: any) => void;
    storedPreferences: StoredPreferences;
}

type TitleOption = FilterOption & {
    ean13: string;
};

const formatOption = (elements: any) =>
    (elements || []).map((item) => ({ id: item?.id || item.code, name: item.name, ean13: item.ean13 }));

const titleMatcher = (options: TitleOption[], { inputValue }: { inputValue: string }) => {
    const lowercasedInputValue = inputValue.toLowerCase();
    return options.filter((option) => {
        const nameMatch = option.name.toLowerCase().includes(lowercasedInputValue);
        const ean13Match = option.ean13 && option.ean13.toLowerCase().includes(lowercasedInputValue);
        return nameMatch || ean13Match;
    });
};

function TitleSearch({
    titlesPaginationData,
    selection,
    selectTitle,
}: {
    titlesPaginationData: TitlesPaginationData;
    selection?: (string | number)[];
    selectTitle: (titleIds: string[]) => void;
}) {
    const [t] = useTranslation();

    const { titlesAutocomplete } = useAppSelector((state) => state.titles);
    const [titleOptions, setTitleOptions] = React.useState<TitleOption[]>([]);

    useEffect(() => {
        setTitleOptions((prev) => {
            return formatOption(titlesAutocomplete).reduce((acc, item) => {
                const existingOption = acc.find((option) => `${option.id}` === `${item.id}`);
                if (!existingOption) {
                    acc.push(item);
                }
                return acc;
            }, prev);
        });
    }, [titlesAutocomplete]);

    // TODO: fix root of the issue in FiltersAutocomplete
    // Current problem: titleOptions updates while typing which updates chosenOptions
    // which in turn resets the input value (internal behavior of FiltersAutocomplete).
    // Using a "ref" is a workaround to prevent the input from being reset while typing.
    const titleOptionsRef = useRef(titleOptions);
    titleOptionsRef.current = titleOptions;

    const chosenOptions = useMemo<TitleOption[]>(() => {
        return (selection || []).map((selectedId: string | number) => {
            const foundOption = titleOptionsRef.current.find((option) => `${option.id}` === `${selectedId}`);
            return foundOption
                ? { id: `${foundOption.id}`, name: foundOption.name, ean13: foundOption.ean13 }
                : { id: `${selectedId}`, name: t("Analytics.AllTitles"), ean13: "" };
        });
    }, [selection, t]);

    const handleChangeTitlesInput = (e: ChangeEvent<HTMLInputElement>) => {
        titlesPaginationData.handleQueryTitle(e);
    };

    const resetTitleQuery = () => {
        titlesPaginationData.handleQueryTitle({ target: { value: "" } } as ChangeEvent<HTMLInputElement>);
    };

    return (
        <FiltersAutocomplete
            label={<></>}
            placeholder={t("Analytics.AllTitles")}
            filtersOptions={titleOptions}
            chosenOptions={chosenOptions}
            handleChooseOption={(newSelection: TitleOption[]) => {
                selectTitle(newSelection.map((o) => `${o.id}`));
            }}
            unselectOption={(unselectedId: number) => {
                selectTitle(chosenOptions.filter((o) => `${o.id}` !== `${unselectedId}`).map((o) => `${o.id}`));
            }}
            onInputChange={handleChangeTitlesInput}
            onScrollEnd={() => titlesPaginationData.setOffsetPaginationTitles(titleOptions?.length ?? 0)}
            onScrollEndLoading={titlesPaginationData.requestStatusTitle}
            onClose={() => resetTitleQuery()}
            optionAllName={t("Analytics.AllTitles")}
            showOptionAll
            limitTags={3}
            matcher={titleMatcher}
            disableCloseOnSelect
            size="small"
        />
    );
}

function Filters({ periodsDateRange, titlesPaginationData, filters, onOptionSelect, storedPreferences }: IFilters) {
    const { periodsShortcuts } = useDateSelectorShortcuts();
    const dispatch = useAppDispatch();
    const [t] = useTranslation();

    const handleDatesChange = (newDatesRange: IDatesRange) => {
        onOptionSelect("dates", newDatesRange);

        const preferences = { chosenDates: newDatesRange };
        const settingsValue = settingsTransform("filters", preferences, storedPreferences, "consolidated-sales");
        dispatch(putCompanySettings([{ settingsName: "analytics_layout", settingsValue }]));
    };

    return (
        <div className="relative flex flex-col gap-4">
            <div className="flex justify-between items-center">
                <div className="relative flex-1 max-w-[550px]">
                    <TitleSearch
                        titlesPaginationData={titlesPaginationData}
                        selectTitle={(titleIds) => {
                            onOptionSelect("titleIds", titleIds);
                        }}
                        selection={filters?.titleIds}
                    />
                </div>
                {!isObjectEmpty(filters?.dates) && (
                    <DateRangeSelectors
                        dateRange={periodsDateRange}
                        maxEndDate={ANALYTICS_DATE_PICKER_MAX_END}
                        label={t("Analytics.DataBetween")}
                        defaultValues={filters?.dates}
                        rangeShortcuts={periodsShortcuts}
                        handleChanges={(newDatesRange) => newDatesRange && handleDatesChange(newDatesRange)}
                    />
                )}
            </div>
        </div>
    );
}

export default React.memo(Filters);
